// Generated by Claude Code
import { useEffect } from 'react'
import { State, useCustomGlobalState, type PropertySelector } from '#core/state/State.ts'
import PriceRatesApi from '#core/api/operations/PriceRatesApi.ts'
import type { PriceRates } from '#core/entities/operations/shared/Price.ts'
import { useUserState } from '#core/state/UserState.ts'

/** Global in-memory cache of how the company shows prices — display currency and rate tables — that display code converts with; `rates` is null until loaded. */
export class PriceRatesState extends State<PriceRatesState> {
    rates: PriceRates | null = null;
    $isLoading: boolean = false;
    _attempted: boolean = false;

    private readonly _api = new PriceRatesApi();

    /** Loads the rates unless a load is running or was already attempted; safe to call repeatedly. */
    ensureLoaded(): void {
        if (this.$isLoading || this._attempted) return;
        void this._load();
    }

    /** Drops the cache so the next {@link ensureLoaded} refetches. */
    invalidate(): void {
        this.reactTo.rates = null;
        this.reactTo._attempted = false;
    }

    /** Refetches after the default cost rate changed, keeping the current rates shown until the new ones land. */
    reload(): void {
        void this._load();
    }

    private async _load(): Promise<void> {
        this.reactTo.$isLoading = true;
        this.reactTo._attempted = true;
        try {
            this.reactTo.rates = await this._api.getRates();
        } catch {
            // Leaves `rates` null: prices render as missing until a deliberate retry.
        } finally {
            this.reactTo.$isLoading = false;
        }
    }
}

/** Hook exposing the {@link PriceRatesState}, auto-loading the rates once the user is logged in. */
export function usePriceRates(filter: PropertySelector<PriceRatesState> | undefined = undefined) {
    const userState = useUserState();
    const state = useCustomGlobalState(() => new PriceRatesState(undefined, null), 'PriceRatesState', filter);
    useEffect(() => {
        if (userState.loginState === 'failed') {
            state.invalidate();
        } else if (userState.loginState === 'success') {
            state.ensureLoaded();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userState.loginState]);
    return state;
}
